/**
 * SociBaba Intent Radar — the killer feature seller UI.
 *
 * Sellers see who is about to buy their product with 90%+ confidence. Ranked
 * by intent_score (0-100 sigmoid). Contact PII is masked by default; unlock
 * costs 5 credits per buyer (idempotent).
 *
 * Route: /workspace/intent-radar
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radar, Search, Users, TrendingUp, Package, Phone, Mail, MapPin, Sparkles, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { IntentLiveTicker } from "@/components/shop/IntentLiveTicker";
import { PushAlertsCard } from "@/pages/workspace/PushAlertsCard";
import { NudgeTemplatesCard } from "@/pages/workspace/NudgeTemplatesCard";
import { WishlistWatchersCard } from "@/pages/workspace/WishlistWatchersCard";
import { LoyaltyLeaderboardCard } from "@/pages/workspace/LoyaltyLeaderboardCard";
import { SanctaInsightsCard } from "@/pages/workspace/SanctaInsightsCard";

interface Buyer {
  user_id: string;
  name: string;
  email_masked: string | null;
  phone_masked: string | null;
  city: string;
  country: string;
  intent_score: number;
  intent_tier: "very_high" | "high" | "medium" | "low" | "cold";
  last_event_type: string | null;
  last_event_at: string | null;
  counts: Record<string, number>;
  reason: string;
  raw_score: number;
  unlocked: boolean;
}

interface Summary {
  watching_now: number;
  high_intent_buyers: number;
  cart_abandons_24h: number;
  checkout_abandons_24h: number;
  unlocks_lifetime: number;
  top_products: Array<{ product_id: string; title?: string; slug?: string; buyer_count: number }>;
}

interface UnlockedContact {
  name?: string;
  email?: string;
  phone?: string;
  billing_address?: any;
  country?: string;
  fresh_unlock?: boolean;
  credits_charged?: number;
}

const TIER_COLORS: Record<Buyer["intent_tier"], string> = {
  very_high: "bg-emerald-500",
  high:      "bg-emerald-400",
  medium:    "bg-amber-400",
  low:       "bg-orange-300",
  cold:      "bg-neutral-300",
};

const TIER_LABELS: Record<Buyer["intent_tier"], string> = {
  very_high: "Very High",
  high:      "High",
  medium:    "Medium",
  low:       "Low",
  cold:      "Cold",
};

export default function IntentRadarPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [productId, setProductId] = useState<string>("");
  const [buyers, setBuyers] = useState<Buyer[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [unlocks, setUnlocks] = useState<Record<string, UnlockedContact>>({});
  const [productTitle, setProductTitle] = useState<string>("");

  useEffect(() => {
    api.get<Summary>("/intent/summary").then(r => setSummary(r.data)).catch(() => {});
  }, []);

  async function searchProduct() {
    if (!productId.trim()) return;
    setLoading(true);
    setBuyers(null);
    try {
      const { data } = await api.get(`/intent/product/${encodeURIComponent(productId.trim())}`);
      setBuyers(data.buyers || []);
      setProductTitle(data.product?.title || "");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not load buyers for that product.");
    } finally {
      setLoading(false);
    }
  }

  async function unlockContact(buyerUserId: string) {
    if (!productId.trim()) return;
    try {
      const { data } = await api.post(`/intent/unlock`, {
        product_id: productId.trim(),
        buyer_user_id: buyerUserId,
      });
      setUnlocks(u => ({ ...u, [buyerUserId]: data }));
      if (data.fresh_unlock) {
        toast.success(`Contact unlocked · ${data.credits_charged} credits used`);
      } else {
        toast.info("Contact already unlocked previously (no charge)");
      }
      // Mark this buyer as unlocked in the local list so the padlock disappears.
      setBuyers(bs => bs?.map(b => b.user_id === buyerUserId ? { ...b, unlocked: true } : b) || null);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || "Unlock failed. Check your credits.";
      toast.error(msg);
    }
  }

  function pickTopProduct(id: string) {
    setProductId(id);
    setTimeout(searchProduct, 50);
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-orange-500 to-fuchsia-600 grid place-items-center text-white">
            <Radar className="h-5 w-5" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="intent-radar-title">
            Intent Radar
          </h1>
          <span className="ml-2 text-xs uppercase tracking-widest text-neutral-500">Powered by SociBaba AI</span>
        </div>
        <p className="text-neutral-600 text-sm max-w-2xl mb-8">
          See who is about to buy each of your products. Ranked live by 39 behavioral signals — cart
          abandoners, checkout leavers, wishlist adders. Unlock full contact for 5 credits per buyer.
        </p>

        {/* KPI strip */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="intent-kpi">
            <Kpi icon={<Users className="h-4 w-4" />}  label="Watching now" value={summary.watching_now} pulse />
            <Kpi icon={<TrendingUp className="h-4 w-4" />} label="High-intent buyers" value={summary.high_intent_buyers} />
            <Kpi icon={<Package className="h-4 w-4" />} label="Cart abandons 24h" value={summary.cart_abandons_24h} />
            <Kpi icon={<Sparkles className="h-4 w-4" />} label="Checkout abandons 24h" value={summary.checkout_abandons_24h} />
            <Kpi icon={<Lock className="h-4 w-4" />} label="Unlocks lifetime" value={summary.unlocks_lifetime} />
          </div>
        )}

        {/* Push-to-Seller Alerts config + recent activity */}
        <PushAlertsCard />

        {/* Sancta Demand Pulse — what shoppers are actually asking, right now */}
        <SanctaInsightsCard />

        {/* Buyer Nudge Templates (WhatsApp/SMS canned replies) */}
        <NudgeTemplatesCard />

        {/* Wishlist Watchers — live count of shoppers eyeing each SKU */}
        <WishlistWatchersCard />

        {/* Loyalty Leaderboard — Bronze/Silver/Gold scouts across all your AI Carts */}
        <LoyaltyLeaderboardCard />

        {/* Search + top products */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/15">
              <Search className="h-4 w-4 text-neutral-500" />
              <input
                value={productId}
                onChange={e => setProductId(e.target.value)}
                onKeyDown={e => e.key === "Enter" && searchProduct()}
                placeholder="Enter product_id or slug"
                data-testid="intent-product-input"
                className="flex-1 py-3 text-sm bg-transparent outline-none"
              />
              <button
                onClick={searchProduct}
                data-testid="intent-search-btn"
                className="h-9 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white text-sm font-medium hover:brightness-110 disabled:opacity-50"
                disabled={loading || !productId.trim()}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find buyers"}
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-neutral-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Your top intent products</div>
            <div className="space-y-1">
              {(summary?.top_products || []).length === 0 && (
                <div className="text-xs text-neutral-500">No high-intent activity yet. Once buyers start browsing your catalog, they&apos;ll appear here.</div>
              )}
              {(summary?.top_products || []).map(p => (
                <button
                  key={p.product_id}
                  onClick={() => pickTopProduct(p.product_id)}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-neutral-50 text-xs flex items-center justify-between gap-2"
                  data-testid={`intent-top-${p.product_id}`}
                >
                  <span className="truncate">{p.title || p.product_id}</span>
                  <span className="text-neutral-500 font-semibold shrink-0">{p.buyer_count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Buyers list */}
        {buyers && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            {/* Live ticker for this product — polls every 10s */}
            <IntentLiveTicker productId={productId.trim()} className="mb-4" />
            <div className="text-sm text-neutral-600 mb-3">
              {buyers.length === 0
                ? "No buyers meet the intent threshold yet. Try lowering the threshold or wait for signals to build."
                : <>
                    <span className="font-semibold text-neutral-900">{buyers.length}</span> buyers currently rank ≥ 40 intent for
                    {" "}<span className="font-medium">{productTitle || productId}</span>.
                  </>}
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden" data-testid="intent-buyers-list">
              {buyers.map((b, idx) => {
                const unlocked = b.unlocked || !!unlocks[b.user_id];
                const contact = unlocks[b.user_id];
                return (
                  <div
                    key={b.user_id}
                    className={`p-4 border-b border-neutral-100 last:border-b-0 flex items-start gap-4 ${idx === 0 ? "bg-orange-50/30" : ""}`}
                    data-testid={`intent-buyer-${b.user_id}`}
                  >
                    {/* Score orb */}
                    <div className="shrink-0 flex flex-col items-center">
                      <div className={`h-14 w-14 rounded-full grid place-items-center text-white font-semibold text-lg ${TIER_COLORS[b.intent_tier]}`}>
                        {b.intent_score}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{TIER_LABELS[b.intent_tier]}</div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-neutral-900 truncate">{b.name}</span>
                        {b.city && (
                          <span className="text-xs text-neutral-500 inline-flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {b.city}{b.country ? ` · ${b.country}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-600 mt-0.5">{b.reason}</div>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-neutral-500">
                        {b.email_masked && !unlocked && (
                          <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {b.email_masked}</span>
                        )}
                        {b.phone_masked && !unlocked && (
                          <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {b.phone_masked}</span>
                        )}
                        {unlocked && contact && (
                          <>
                            {contact.email && <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><Mail className="h-3 w-3" /> {contact.email}</span>}
                            {contact.phone && <span className="inline-flex items-center gap-1 text-emerald-700 font-medium"><Phone className="h-3 w-3" /> {contact.phone}</span>}
                          </>
                        )}
                      </div>
                    </div>

                    <div className="shrink-0 flex flex-col items-end gap-2">
                      {!unlocked ? (
                        <button
                          onClick={() => unlockContact(b.user_id)}
                          data-testid={`intent-unlock-${b.user_id}`}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-neutral-900 text-white hover:bg-neutral-800 inline-flex items-center gap-1.5"
                        >
                          <Lock className="h-3 w-3" /> Unlock · 5 credits
                        </button>
                      ) : (
                        <>
                          <a
                            href={contact?.phone ? `tel:${contact.phone}` : "#"}
                            className="px-3 py-1.5 rounded-lg text-xs bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1.5"
                          >
                            <Phone className="h-3 w-3" /> Call now
                          </a>
                          {contact?.phone && (
                            <a
                              href={`https://wa.me/${contact.phone.replace(/[^\d+]/g, "")}`}
                              target="_blank" rel="noopener noreferrer"
                              className="px-3 py-1.5 rounded-lg text-xs bg-emerald-500 text-white hover:bg-emerald-600"
                            >WhatsApp</a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ icon, label, value, pulse }: { icon: React.ReactNode; label: string; value: number; pulse?: boolean }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-neutral-500 text-[11px] uppercase tracking-widest">
        <span className={pulse ? "text-emerald-500" : ""}>{icon}</span>
        <span>{label}</span>
        {pulse && <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse ml-auto" />}
      </div>
      <div className="text-2xl font-semibold text-neutral-900 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}
